'use client';

import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useAddToCart } from '@/hooks/use-add-to-cart.hook';
import { isOnlineConsultationSearch } from '@/app/(main)/(pages)/busca/[slug]/_utils/is-online-consultation-search.util';
import { useCartAdditionConfirmation } from '@/hooks/use-cart-addition-confirmation.hook';
import { maskPhone } from '@/utils/formatters/phonenumber';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { STANDALONE_GUIDE_SCHEDULE_TYPE } from '@/types/enums/analytics/search/search-analytics.enum';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { CartItemBody } from '@/types/cart.types';
import { PatientOption } from '@/types/patient-selection.types';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';

export function buildWarningMessage(phone: string): string {
  const suffix = phone ? ` ${phone}` : '';
  return `O agendamento deve estar previamente confirmado com o estabelecimento pelo número${suffix}.`;
}

export function buildStandaloneCartItems(schedule: SearchPartnerData, patientId: string): CartItemBody[] {
  return schedule.procedures
    .filter((procedure) => Boolean(procedure.price))
    .map((procedure) => ({
      procedureId: procedure.id,
      patientId,
      partnerFacilityId: schedule.partner.facility.id,
      professionalId: schedule.professional?.id || undefined,
    }));
}

interface SearchCardTabStandaloneProps {
  schedule: SearchPartnerData;
}

export function SearchCardTabStandalone({ schedule }: SearchCardTabStandaloneProps) {
  const [firstPhone] = schedule.partner.facility.contacts.phones;
  const phone = firstPhone ? maskPhone(firstPhone) : '';
  const warningMessage = buildWarningMessage(phone);

  const { requestPatientSelection } = usePatientSelection();
  const { addToCart, isAdding } = useAddToCart();
  const { confirmCartAddition } = useCartAdditionConfirmation();
  const analytics = getAnalyticsPlugin();

  const partnerId = schedule.partner.id;
  const price = sumProcedurePrices(schedule.procedures);

  const handleSelectPatient = async (patient: PatientOption) => {
    analytics.track(AnalyticsEnum.STANDALONE_SELECT_PATIENT, { paciente_tipo: patient.type });

    const { wasAdded, eventInternalId } = await addToCart(buildStandaloneCartItems(schedule, patient.id));

    if (wasAdded) {
      analytics.track(AnalyticsEnum.STANDALONE_ADD_TO_CART, {
        partner_id: partnerId,
        price,
        schedule_type: STANDALONE_GUIDE_SCHEDULE_TYPE,
        eventInternalId,
      });
    }
  };

  const handleAddGuide = () => {
    analytics.track(AnalyticsEnum.CLICK_ADD_STANDALONE_GUIDE, { partner_id: partnerId });

    requestPatientSelection({
      scheduleType: STANDALONE_GUIDE_SCHEDULE_TYPE,
      onBeforeSelection: () => confirmCartAddition(
        {
          partnerId: schedule.partner.id,
          facility: schedule.partner.facility,
          isOnline: isOnlineConsultationSearch(schedule),
        },
        STANDALONE_GUIDE_SCHEDULE_TYPE,
        { isStandalone: true },
      ),
      onSelectPatient: handleSelectPatient,
    });
  };

  return (
    <div className="flex flex-col gap-2" data-testid={DataTestIdEnum.SEARCH_CARD_TAB_STANDALONE}>
      <Typography
        variant={TypographyVariant.MEDIUM_BODY_TEXT}
        className="font-semibold"
        data-testid={DataTestIdEnum.SEARCH_CARD_STANDALONE_DESCRIPTION}
      >
        Use esta opção para incluir exames esquecidos, complementares ou gerar uma guia de encaixe.
      </Typography>

      <div
        className="flex w-full items-start gap-3 rounded-2xl border border-warning-300 bg-warning-100 py-3 pl-3 pr-4 shadow-floating"
        data-testid={DataTestIdEnum.SEARCH_CARD_STANDALONE_WARNING}
      >
        <div className="flex size-6 shrink-0 items-center justify-center">
          <Icon icon={faCircleExclamation} className="text-xl text-warning-600" />
        </div>
        <p className="text-base font-semibold text-warning-800">{warningMessage}</p>
      </div>

      <Button
        startIcon={faPlus}
        startIconSize="1x"
        startIconClassName="text-base"
        className="py-2 text-base"
        data-testid={DataTestIdEnum.SEARCH_CARD_STANDALONE_ADD_BUTTON}
        isLoading={isAdding}
        onClick={handleAddGuide}
      >
        Adicionar guia avulsa
      </Button>
    </div>
  );
}
